import React from 'react';
import {
  Box,
  Typography,
  Card,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import PaginationBar from '../../common/PaginationBar';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  TableChart as TableChartIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { SHARED_TYPOGRAPHY, getProgressStyles } from '../../../theme';
import { PAGE_SIZE_OPTIONS } from '../../../config/constants';

/**
 * TemplateSummaryTable Component
 *
 * Displays a paginated table of templates with their performance metrics.
 * Supports server-side pagination, filtering, search, and sorting for all-time view.
 */
const TemplateSummaryTable = ({
  templatePerformance,
  isAllTime,
  // Pagination state
  templatePerformancePage,
  templatePerformancePageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  // Filter state
  hideZeroActivity,
  onHideZeroActivityChange,
  selectedCategory,
  // Search state
  templateSearch,
  onTemplateSearchChange,
  // Sort state
  templateSortBy,
  templateSortOrder,
  onSortByChange,
  onSortOrderToggle,
  // Data
  paginatedTemplates,
}) => {
  return (
    <Box sx={{ mt: 4 }}>
      <Accordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="template-summary-content"
          id="template-summary-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TableChartIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
              Template Summary
            </Typography>
            <Typography
              variant="h6"
              sx={{ ...SHARED_TYPOGRAPHY.bodyMediumSecondary }}
            >
              ({templatePerformance?.period || 'N/A'})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            {/* Search Input - Only for all-time view */}
            {isAllTime && (
              <TextField
                size="small"
                placeholder="Search templates (min 3 chars)..."
                value={templateSearch}
                onChange={e => onTemplateSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  templateSearch.length > 0 && templateSearch.length < 3
                    ? 'Enter at least 3 characters to search'
                    : ''
                }
                sx={{ minWidth: 300, width: 400, maxWidth: 500 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideZeroActivity}
                  onChange={e => onHideZeroActivityChange(e.target.checked)}
                />
              }
              label="Hide zero-activity"
            />
            <Typography variant="body2" sx={SHARED_TYPOGRAPHY.bodySecondary}>
              Show:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={templatePerformancePageSize}
                onChange={e => onPageSizeChange(Number(e.target.value))}
                displayEmpty
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={SHARED_TYPOGRAPHY.bodySecondary}>
              entries
            </Typography>
            {/* Sort Controls - Only for all-time view */}
            {isAllTime && (
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={templateSortBy}
                    label="Sort By"
                    onChange={e => onSortByChange(e.target.value)}
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="executions">Executions</MenuItem>
                    <MenuItem value="success_rate">Success Rate</MenuItem>
                    <MenuItem value="cost">Cost</MenuItem>
                    <MenuItem value="last_activity">Last Activity</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip
                  title={`Sort ${templateSortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                >
                  <IconButton
                    size="small"
                    onClick={onSortOrderToggle}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    {templateSortOrder === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          <TableContainer component={Card}>
            <Table
              key={`template-summary-${templatePerformancePageSize}-${templatePerformancePage}`}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell align="right">Executions</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                  <TableCell align="right">Avg Time (s)</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTemplates.map(template => (
                  <TableRow key={template.template_id}>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={SHARED_TYPOGRAPHY.bodyBold}
                      >
                        {template.template_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={SHARED_TYPOGRAPHY.bodySecondary}
                      >
                        {template.category}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={SHARED_TYPOGRAPHY.bodyMedium}
                      >
                        {template.execution_count.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 1,
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={template.success_rate || 0}
                          sx={getProgressStyles(template.success_rate || 0)}
                        />
                        <Typography
                          variant="body1"
                          sx={SHARED_TYPOGRAPHY.bodyBold}
                        >
                          {(template.success_rate || 0).toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={SHARED_TYPOGRAPHY.bodyMedium}
                      >
                        {(template.average_execution_time || 0).toFixed(2)}s
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={SHARED_TYPOGRAPHY.bodyBold}
                      >
                        ${(template.total_cost || 0).toFixed(4)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ mt: 3 }}>
              <PaginationBar
                currentPage={templatePerformancePage}
                totalPages={totalPages}
                totalCount={
                  templatePerformance?.pagination?.total_count ||
                  paginatedTemplates.length
                }
                pageSize={templatePerformancePageSize}
                onPrev={() =>
                  templatePerformancePage > 1 &&
                  onPageChange(templatePerformancePage - 1)
                }
                onNext={() =>
                  templatePerformancePage < totalPages &&
                  onPageChange(templatePerformancePage + 1)
                }
                onPageClick={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TemplateSummaryTable;
